import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <a name="module-storage-file" id="module-storage-file"></a>
    <h1 {...{
      "id": "file"
    }}>{`File`}</h1>
    <p>{`Represents a file on a file system. Provides methods for reading from and
writing to the file. You'll never instantiate a `}<inlineCode parentName="p">{`File`}</inlineCode>{` directly; instead
you'll get access via a `}<a parentName="p" {...{
        "href": "./FileSystemProvider"
      }}>{`storage.FileSystemProvider`}</a>{`.
Keep in mind that `}<inlineCode parentName="p">{`File`}</inlineCode>{` as such doesn't need a `}<inlineCode parentName="p">{`require()`}</inlineCode>{` statement, however a `}<inlineCode parentName="p">{`localFileSystem`}</inlineCode>{` will need it.`}</p>
    <p><strong parentName="p">{`Example`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// Get the object of a File instance
const fs = require('uxp').storage.localFileSystem;
const file = await fs.createEntryWithUrl("file:/Users/user/Documents/tmp"); // Gets a File instance
console.log(file.isFile); // returns true
`}</code></pre>
    <a name="module-storage-file-isfile" id="module-storage-file-isfile"></a>
    <h2 {...{
      "id": "isfile"
    }}>{`isFile`}</h2>
    <p>{`Indicates that this instance is a file.`}</p>
    <p><strong parentName="p">{`Example`}</strong>{`  `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`if (anEntry.isFile) {
    await anEntry.read();
}
`}</code></pre>
    <a name="module-storage-file-mode" id="module-storage-file-mode"></a>
    <h2 {...{
      "id": "mode--symbol"
    }}>{`mode : `}<inlineCode parentName="h2">{`Symbol`}</inlineCode></h2>
    <p>{`Indicates whether this file is read-only or read-write. See `}<a parentName="p" {...{
        "href": "./modes#readonly--symbol"
      }}>{`readOnly`}</a>{` and `}<a parentName="p" {...{
        "href": "./modes#readwrite--symbol"
      }}>{`readWrite`}</a>{`.`}</p>
    <p><strong parentName="p">{`Example`}</strong>{`  `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`if (aFile.mode === modes.readOnly) {
    throw new Error("Can't write to a file opened as read-only.");
}
`}</code></pre>
    <a name="module-storage-file-read" id="module-storage-file-read"></a>
    <h2 {...{
      "id": "readoptions"
    }}>{`read(options)`}</h2>
    <p>{`Reads data from the file and returns it. The file format can be specified
with the `}<inlineCode parentName="p">{`format`}</inlineCode>{` option. If a format is not supplied, the file is assumed
to be a text file using UTF8 encoding.`}</p>
    <p><strong parentName="p">{`Returns`}</strong>{`: `}<inlineCode parentName="p">{`Promise<string|ArrayBuffer>`}</inlineCode>{` the contents of the file  `}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Param`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`options`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`any`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`[options.format]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Symbol`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`formats.utf8`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The format of the file; see `}<a parentName="td" {...{
              "href": "./formats#utf8--symbol"
            }}>{`utf8`}</a>{` and `}<a parentName="td" {...{
              "href": "./formats#binary--symbol"
            }}>{`binary`}</a>{`.`}</td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example`}</strong>{`  `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const text = await myNovel.read();
`}</code></pre>
    <p><strong parentName="p">{`Example`}</strong>{`  `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const data = await myNovel.read({format: formats.binary});
`}</code></pre>
    <a name="module-storage-file-write" id="module-storage-file-write"></a>
    <h2 {...{
      "id": "writedata-options"
    }}>{`write(data, options)`}</h2>
    <p>{`Writes data to a file, appending if desired. The format of the file
is controlled via the `}<inlineCode parentName="p">{`format`}</inlineCode>{` option, and defaults to UTF8.`}</p>
    <p><strong parentName="p">{`Returns`}</strong>{`: `}<inlineCode parentName="p">{`Promise<number>`}</inlineCode>{` -  the length of the contents written to the file`}<br parentName="p"></br>{`
`}<strong parentName="p">{`Throws`}</strong>{`:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`FileIsReadOnly`}</inlineCode>{` if writing to a read-only file`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`OutOfSpace`}</inlineCode>{` If writing to the file causes the file system to exceed the available space (or quota)`}</li>
    </ul>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Param`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`data`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`ArrayBuffer`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the data to write to the file`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`options`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`any`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`[options.format]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`Symbol`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`formats.utf8`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the format of the file; see `}<a parentName="td" {...{
              "href": "./formats#utf8--symbol"
            }}>{`utf8`}</a>{` and `}<a parentName="td" {...{
              "href": "./formats#binary--symbol"
            }}>{`binary`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`[options.append]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`if `}<inlineCode parentName="td">{`true`}</inlineCode>{`, the data is written to the end of the file`}</td>
        </tr>
      </tbody>
    </table>
    <p><strong parentName="p">{`Example`}</strong>{`  `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`await myNovel.write("It was a dark and stormy night.\\n");
await myNovel.write("Cliches and tropes aside, it really was.", {append: true});
`}</code></pre>
    <p><strong parentName="p">{`Example`}</strong>{`  `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const data = new ArrayBuffer();
await aDataFile.write(data, {format: formats.binary});
`}</code></pre>
    <a name="module-storage-file-isfile" id="module-storage-file-isfile"></a>
    <h2 {...{
      "id": "isfileentry"
    }}>{`isFile(entry)`}</h2>
    <p>{`Determines if the entry is a file or not. This is safe to use even if the
entry is `}<inlineCode parentName="p">{`null`}</inlineCode>{` or `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Returns`}</strong>{`: `}<inlineCode parentName="p">{`boolean`}</inlineCode>{` - if `}<inlineCode parentName="p">{`true`}</inlineCode>{`, the entry is a file.  `}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Param`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`entry`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`any`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`the entry to check`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      